import { useId, type ReactNode } from 'react'
import type { ScoreHistory } from '../index.js'
import type { ShiftOutcome } from '../../partner/index.js'
import type { WorkerNotification } from '../../messages/index.js'
import type { WorkerTier } from '../../core/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { ScoreRing } from './ScoreRing.js'
import { NoScoreRing } from './NoScoreRing.js'
import { formatMonth } from './outcomeGroups.js'
import type { DayRules } from './MonthCalendar.js'
import { buildMonthPanels, type MonthPanelData } from './monthPanelData.js'

interface MonthPanelProps {
  panel: MonthPanelData
  /** The tier the worker holds now - what the running month's dial is drawn in, since a tier only changes at close. */
  heldTier: WorkerTier
  isOpen: boolean
  /** The id of the full view this panel opens, for the control to point at. */
  controls: string
  onToggle: () => void
}

/** One month at a glance - its name, its dial, what kind of score that is - and the whole panel is the control that opens it in full. */
function MonthPanel({ panel, heldTier, isOpen, controls, onToggle }: MonthPanelProps) {
  return (
    <li>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? controls : undefined}
        aria-label={`Show ${formatMonth(panel.month)} in full`}
        onClick={onToggle}
        className={`w-full h-full bg-white border p-3 text-center transition-colors hover:bg-canvas ${isOpen ? 'border-marine ring-1 ring-marine' : panel.isCurrent ? 'border-marine/40' : 'border-marine/15'}`}
      >
        <div className="mb-2 text-[11px] font-bold text-ink">{formatMonth(panel.month)}</div>
        {/* A closed month's dial is in the tier its final placed the worker in; the running month's in the tier they hold, as the main dial above. */}
        {panel.score === null ? <NoScoreRing reason={panel.group ? 'Not scored' : 'No score'} /> : <ScoreRing score={panel.score} tier={panel.tier ?? heldTier} size="sm" />}
        <div className="mt-1.5 text-[9.5px] text-ink-mid">
          {panel.scoreKind === 'running' ? 'Running' : panel.scoreKind === 'final' ? 'Final' : 'No score'}
          {panel.tier && panel.scoreKind === 'final' && ` · ${TIER_LABELS[panel.tier]}`}
          {panel.provisional && ' · provisional'}
        </div>
      </button>
    </li>
  )
}

interface MonthPanelsProps {
  history: ScoreHistory | null
  outcomes: readonly ShiftOutcome[]
  /** Everything the worker has been told; each message lands in the month of the score it was about. */
  notifications: readonly WorkerNotification[]
  /** The tier the worker holds now, for the running month's dial. */
  heldTier: WorkerTier
  rules: DayRules
  /** The month open in full beneath the grid, if any - one at a time. */
  openMonth: string | null
  onOpenMonth: (month: string | null) => void
  /** The open month in full, the same view as the current period. */
  renderOpen: (month: string) => ReactNode
}

/**
 * A worker's months, newest first, each a small panel headed by the month
 * with that month's score dial. Clicking a panel opens the month in full
 * beneath the grid - the same view as the current period, supplied by the
 * caller - and only one month is open at a time, so the grid stays a
 * compact index of the record.
 */
export function MonthPanels({ history, outcomes, notifications, heldTier, rules, openMonth, onOpenMonth, renderOpen }: MonthPanelsProps) {
  const panels = buildMonthPanels(history, outcomes, notifications, rules)
  const openId = `${useId()}-open-month`

  if (panels.length === 0) {
    return <div className="text-[11px] text-ink-mid">No scores, shift outcomes or messages recorded yet.</div>
  }

  return (
    <div className="flex flex-col gap-5">
      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {panels.map((panel) => (
          <MonthPanel key={panel.month} panel={panel} heldTier={heldTier} isOpen={openMonth === panel.month} controls={openId} onToggle={() => onOpenMonth(openMonth === panel.month ? null : panel.month)} />
        ))}
      </ul>
      {openMonth && (
        <div id={openId} className="border-t border-marine/15 pt-5">
          {renderOpen(openMonth)}
        </div>
      )}
    </div>
  )
}
